import React from "react";

import { Typography, Tooltip } from "@mui/material";
import Stack from "@mui/material/Stack";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { CgProfile } from "react-icons/cg";
import { FaCircle } from "react-icons/fa";
import { FiVideo, FiVideoOff } from "react-icons/fi";
import { useStopwatch } from "react-timer-hook";
import {
  LocalAudioTrack,
  LocalVideoTrack,
  Participant,
  RemoteAudioTrack,
  RemoteVideoTrack,
} from "twilio-video";

import AudioLevelIndicator from "../AudioLevelIndicator/AudioLevelIndicator";
import BookmarkButton from "../Buttons/BookmarkButton/BookmarkButton";
import CommentButton from "../Buttons/CommentButton/CommentButton";
import ScreenshotButton from "../Buttons/ScreenshotButton/ScreenshotButton";
import useIsRecording from "../hooks/useIsRecording/useIsRecording";
import useIsTrackSwitchedOff from "../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff";
import useParticipantIsReconnecting from "../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting";
import usePublications from "../hooks/usePublications/usePublications";
import useScreenShareParticipant from "../hooks/useScreenShareParticipant/useScreenShareParticipant";
import useTrack from "../hooks/useTrack/useTrack";
import useVideoContext from "../hooks/useVideoContext/useVideoContext";
import NetworkQualityLevel from "../NetworkQualityLevel/NetworkQualityLevel";
import useStyles from "./styles";

interface MainParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  isLocal: boolean;
}

export default function MainParticipantInfo({
  participant,
  children,
  isLocal,
}: MainParticipantInfoProps) {
  const classes = useStyles();
  const { room } = useVideoContext();
  const { localParticipant } = room!;
  const { t: translate } = useTranslation();

  const { seconds, minutes, hours } = useStopwatch({ autoStart: true });
  const isRecording = useIsRecording();

  const screenShareParticipant = useScreenShareParticipant();
  const isRemoteParticipantScreenSharing =
    screenShareParticipant && screenShareParticipant !== localParticipant;

  const publications = usePublications(participant);

  const videoPublication = publications.find((p) => p.kind === "video");
  const screenSharePublication = publications.find((p) => p.trackName.includes("screen"));

  const videoTrack = useTrack(screenSharePublication || videoPublication);
  const hasVideoTrack = Boolean(videoTrack);

  const audioPublication = publications.find((p) => p.kind === "audio");
  const audioTrack = useTrack(audioPublication) as LocalAudioTrack | RemoteAudioTrack | undefined;

  const isVideoSwitchedOff = useIsTrackSwitchedOff(
    videoTrack as LocalVideoTrack | RemoteVideoTrack
  );
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  const trackIsEnabled = () => {
    if (!isLocal) {
      const remoteTrack = videoTrack as RemoteVideoTrack | null;

      return remoteTrack && remoteTrack?.isEnabled !== undefined ? remoteTrack.isEnabled : false;
    }

    return true;
  };

  const RecordTime = (
    <Tooltip
      placement="top"
      title={isRecording ? translate("recordingVS") : translate("notRecording")}
    >
      <div className={classes.recordTime}>
        <FaCircle color={isRecording ? "#D92D20" : "#475467"} className={classes.recordIcon} />
        <span className={classes.timeText}>
          {hours < 10 && "0"}
          {hours}:
        </span>
        <span className={classes.timeText}>
          {minutes < 10 && "0"}
          {minutes}:
        </span>
        <span className={classes.timeText}>
          {seconds < 10 && "0"}
          {seconds}
        </span>
      </div>
    </Tooltip>
  );

  const InfoContainer = (
    <div className={classes.infoContainer}>
      <div className={classes.infoContainerContent}>
        <div className={classes.infoContainerHeader}>
          <NetworkQualityLevel
            participant={localParticipant}
            isLocalMainParticipant={participant === localParticipant}
          />
          {RecordTime}
          <Stack direction="row" spacing={1} alignItems="center" className={classes.identity}>
            {!hasVideoTrack || isVideoSwitchedOff || participant === localParticipant ? (
              <FiVideoOff color="#475467" />
            ) : (
              <FiVideo color="#475467" />
            )}
            <AudioLevelIndicator
              audioTrack={audioTrack}
              isLocalMainParticipant={participant === localParticipant}
            />
          </Stack>
        </div>
        {(!hasVideoTrack || isVideoSwitchedOff || participant === localParticipant) && (
          <div className={classes.avatarContainer}>
            <CgProfile color="#FFF" />
          </div>
        )}
        <div className={classes.infoContainerFooter}>
          <ScreenshotButton hours={hours} minutes={minutes} seconds={seconds} />

          <BookmarkButton hours={hours} minutes={minutes} seconds={seconds} />

          <CommentButton hours={hours} minutes={minutes} seconds={seconds} />
        </div>
      </div>
    </div>
  );

  const renderVideoTracks = () => {
    if (hasVideoTrack && trackIsEnabled()) {
      return children;
    }

    return null;
  };

  return (
    <div
      data-cy-main-participant
      data-cy-participant={participant.identity}
      className={clsx(classes.container, {
        [classes.fullWidth]: !isRemoteParticipantScreenSharing,
      })}
    >
      {InfoContainer}
      {isParticipantReconnecting && (
        <div className={classes.reconnectingContainer}>
          <Typography variant="body1" style={{ color: "white" }}>
            Reconnecting...
          </Typography>
        </div>
      )}
      {renderVideoTracks()}
    </div>
  );
}
